import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { CaseApiService } from '../../../core/services/case-api.service';
import { CaseContextService } from '../../../core/services/case-context.service';
import { Case } from '../../../core/services/case.model';
import { TOPBAR_POPOVER_CLOSE } from '../topbar-popover.service';

@Component({
  selector: 'app-cases-dropdown',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="dropdown-head">
      <span class="title">Cases <span class="count">{{ cases().length }}</span></span>
      <button class="close" type="button" (click)="close()">×</button>
    </div>

    <div class="case-list">
      @if (cases().length === 0) {
        <div class="empty">No cases yet</div>
      } @else {
        @for (c of cases(); track c.id) {
          <div class="case-card" [class.active]="activeCaseId() === c.id" (click)="onCaseClick(c)"
               [title]="activeCaseId() === c.id ? 'Active case' : 'Click to load this case'">
            <div class="cc-main">
              <div class="case-id">{{ c.caseCode }}</div>
              <div class="client">{{ c.name }}</div>
              <div class="meta"><span class="dot"></span>{{ c.stage }}</div>
            </div>
            <button class="cc-open" type="button" [title]="'Open ' + c.name + ' details'"
                    (click)="onOpenCase(c, $event)">
              <span class="cc-open-label">Open</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                   stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M9 18l6-6-6-6"/>
              </svg>
            </button>
          </div>
        }
      }
    </div>

    <div class="dropdown-foot">
      <a class="view-all" (click)="onViewAll()">View all cases →</a>
    </div>
  `,
  styles: [`
    :host { display: flex; flex-direction: column; width: 360px; }
    .dropdown-head, .case-list, .dropdown-foot { background: #fff; width: 360px; }

    .dropdown-head {
      display: flex; align-items: center; justify-content: space-between;
      padding: 10px 14px; border: 1px solid #D9E1EA; border-bottom: 1px solid #E8EDF3;
      border-radius: 10px 10px 0 0;
      background: linear-gradient(180deg, #FCFDFE 0%, #F5F8FB 100%);
    }
    .title { font-size: 12px; font-weight: 700; color: var(--navy); }
    .count { font-family: 'Roboto Mono', ui-monospace, monospace; font-size: 10px; font-weight: 500; color: var(--muted); margin-left: 4px; }
    .close {
      width: 20px; height: 20px; border-radius: 50%; border: none;
      background: rgba(13, 42, 69, 0.08); color: var(--navy); font-size: 12px;
      cursor: pointer; display: grid; place-items: center; font-family: inherit; padding: 0; line-height: 1;
      &:hover { background: var(--neg, #D04A4A); color: #fff; }
    }

    .case-list {
      border-left: 1px solid #D9E1EA; border-right: 1px solid #D9E1EA;
      max-height: 360px; overflow-y: auto; padding: 6px 0;
      display: flex; flex-direction: column; gap: 4px;
    }
    .empty { padding: 24px 14px; font-size: 11px; color: var(--muted); text-align: center; font-style: italic; }

    .case-card {
      display: flex; align-items: center; gap: 8px;
      background: var(--panel, #fff); border: 1px solid transparent; border-radius: 3px;
      padding: 6px 8px 6px 14px; cursor: pointer; margin: 0 8px;
      transition: background 0.12s, border-color 0.12s;
      &:hover { background: var(--bg, #F6F8FB); }
      &:hover .cc-open, &.active .cc-open { opacity: 1; }
      &.active {
        background: var(--blue-soft, #EAF1F9); border-color: var(--blue-border, #B9D2EC);
        border-left: 3px solid var(--blue, #2A6AA8); padding-left: 11px;
      }
    }
    .cc-main { flex: 1; min-width: 0; }
    .cc-open {
      flex-shrink: 0; display: inline-flex; align-items: center; gap: 2px;
      height: 24px; padding: 0 6px 0 8px; border: 1px solid var(--blue-border, #B9D2EC);
      border-radius: 12px; background: #fff; color: var(--blue, #2A6AA8);
      font-family: inherit; font-size: 10px; font-weight: 700; cursor: pointer;
      opacity: 0; transition: opacity 0.12s, background 0.12s, color 0.12s;
      svg { width: 12px; height: 12px; display: block; }
      &:hover { background: var(--blue, #2A6AA8); color: #fff; border-color: var(--blue, #2A6AA8); }
    }
    .case-id { font-family: 'Roboto Mono', ui-monospace, monospace; font-size: 9px; font-weight: 600; color: var(--muted); letter-spacing: 0.04em; }
    .client { font-size: 12px; font-weight: 600; color: var(--navy); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 2px; }
    .meta { display: flex; align-items: center; gap: 5px; font-size: 10px; font-weight: 500; color: var(--muted); margin-top: 2px;
      .dot { display: inline-block; width: 6px; height: 6px; border-radius: 50%; background: var(--muted); flex-shrink: 0; }
    }

    .dropdown-foot {
      display: flex; align-items: center; justify-content: flex-end;
      padding: 10px 14px; border: 1px solid #D9E1EA; border-top: 1px solid #E8EDF3;
      border-radius: 0 0 10px 10px;
      background: linear-gradient(180deg, #F5F8FB 0%, #FCFDFE 100%);
    }
    .view-all { font-size: 11px; font-weight: 600; color: var(--blue); cursor: pointer; &:hover { text-decoration: underline; } }
  `],
})
export class CasesDropdownComponent implements OnInit {
  private readonly closeFn = inject(TOPBAR_POPOVER_CLOSE);
  private readonly caseApi = inject(CaseApiService);
  private readonly caseCtx = inject(CaseContextService);
  private readonly router = inject(Router);

  protected readonly cases = signal<Case[]>([]);
  protected readonly activeCaseId = computed(() => this.caseCtx.activeCase()?.id ?? null);

  ngOnInit(): void {
    this.caseApi.list().subscribe({ next: (rows) => this.cases.set(rows), error: () => this.cases.set([]) });
  }

  protected close(): void { this.closeFn(); }

  protected onCaseClick(c: Case): void { this.caseCtx.setActive(c); this.closeFn(); }

  protected onOpenCase(c: Case, event: Event): void {
    event.stopPropagation();
    this.caseCtx.setActive(c);
    this.closeFn();
    this.router.navigateByUrl(`/broker/cases/${c.id}`);
  }

  protected onViewAll(): void { this.closeFn(); this.router.navigateByUrl('/broker/cases'); }
}
